import { appVariant } from '@util/AppUtils';
import { Auth } from '@util/Auth';
import { generateUniqueTestEmail } from '@util/Crypto';
import {
  MenoUserData,
  NervaUserData,
  QuitUserData,
  SurveyUpdatedBy,
  UserData,
} from 'hydra-data-types';
import { PageStackState } from './pageStack/PageStackState';
import { AssessmentUser, IncompleteUser } from './user/UserState';

export const mockName = 'Harry';
export const mockLastName = 'Potter';
export const mockEmail = 'harry.potter@example.com';
const mockPassword = 'abc123$%^';
const mockSurveyUpdatedAt = 1647486800188;

export const mockPageStackUpToRecap: PageStackState = [
  'gender',
  'age',
  'doctor',
  'attribution',
  'howLong',
  'whyNerva',
  'triggers',
  'symptoms',
  'impactLife',
  'howOften',
  'ibsCosts',
  'treatments',
  'didTreatmentsWork',
  'doCertainFoodsTrigger',
  'doesStress',
  'howLikeToFeel',
  'ifFreeFromIbs',
  'areYouConfident',
  'liveFreeNow',
  'userInfoRecap',
];

export const mockPageStackUpToAuth: PageStackState = [...mockPageStackUpToRecap, 'auth'];

export const mockPageStackUpToPayment: PageStackState = [
  ...mockPageStackUpToAuth,
  'loadingResults',
  'nervaProgramStructure',
  'programBenefits',
  'nervaCoreProgramExperience',
  'programSupport',
  'maintenanceExperience',
  'payment',
];

export const mockPageStackUpToTrial: PageStackState = [
  ...mockPageStackUpToAuth,
  'loadingResults',
  'nervaProgramStructure',
  'programBenefits',
  'nervaCoreProgramExperience',
  'programSupport',
  'maintenanceExperience',
];

export const mockNervaUserData: NervaUserData = {
  gender: ['Non-binary'],
  // Both live Nerva flows ask date of birth; the coverage-check save needs it alongside age.
  dateOfBirth: ['2004-01-15'],
  age: ['22'],
  doctor: ['No'],
  attribution: ['Instagram'],
  howLong: ['6-12 months'],
  whyNerva: ['My symptoms have become worse'],
  triggers: ['Medication'],
  symptoms: ['Abdominal pain'],
  impactLife: ['Can’t eat foods I love', 'Nervous to socialize'],
  howOften: ['Daily'],
  ibsCosts: ['Increased insurance premiums'],
  treatments: ['Probiotics'],
  didTreatmentsWork: ['Yes'],
  doCertainFoodsTrigger: ['Sort of'],
  doesStress: ['Not sure'],
  howLikeToFeel: ['Physically comfortable'],
  ifFreeFromIbs: ['Eat the foods I love', 'Enjoy social life/relationships'],
  areYouConfident: ['Confident'],
  liveFreeNow: ['Important'],
  surveyUpdatedBy: SurveyUpdatedBy.Web,
  surveyUpdatedAt: mockSurveyUpdatedAt,
};

export const mockMenoUserData: MenoUserData = {
  programIntent: ['Night sweats'],
  classify: ['menopausal'],
  age: ['45'],
  hotFlash: ['1'],
  hotFlashIntensity: ['Mild'],
  hotFlashSymptoms: ['Perspiration'],
  sleepDifficulty: ['SleepDifficulty'],
  nightSweats: ['Yes'],
  nightSweatsHowMany: ['2'],
  sleepDisruption: ['Less than 1 month'],
  sleepQuality: ['Terrible'],
  sleepImpact: ['Work'],
  struggleWork: ['Yes'],
  struggleRelationship: ['No'],
  doctor: ['Yes'],
  attribution: ['Search engine (Google, Bing)'],
  hormones: ['No'],
  naturalToolsTried: ['Exercise changes'],
  naturalToolsEfectiveness: ['Not effective'],
  nonHormonalTool: ['Not important'],
  hotFlashesHypnotherapy: ['No'],
  surveyUpdatedBy: SurveyUpdatedBy.Web,
  surveyUpdatedAt: mockSurveyUpdatedAt,
};

const mockQuitUserData: QuitUserData = {
  gender: ['Female'],
  age: ['30s'],
  howLong: ['6-12 months'],
  quitAttempts: ['5-9 times'],
  longestQuitAttempt: ['1 week to 1 month'],
  smokingProducts: ['Tobacco', 'Cigarettes'],
  whyQuitNow: ['My cravings have become worse', 'I’ve tried other methods and they haven’t worked'],
  doctor: ['No'],
  attribution: ['Facebook'],
  firstDailyCigarette: ['5-30 minutes'],
  dailyCigaretteConsumption: ['Half a pack'],
  symptomIntensity: ['Moderate symptoms'],
  forStress: ['Usually'],
  forAnxietyOrDepression: ['Sometimes'],
  forRelaxing: ['Slightly agree'],
  timeOfDayHabit: ['Usually'],
  isAHabit: ['Disagree'],
  symptoms: ['Saggy skin and wrinkles', 'Tooth and gum disease'],
  impactLife: ['Negative self-esteem', 'Anxiety'],
  impactLifeHowOften: ['Weekly'],
  smokingCosts: ['Nicotine replacement therapy (gums, patches etc.)'],
  treatments: ['Cold turkey', 'Prescription medicines'],
  didTreatmentsWork: ['No'],
  howLikeToFeel: ['Healthier in my body', 'Confident in myself'],
  ifFreeFromSmoking: ['Save money', 'Enjoy a healthier body'],
  areYouConfident: ['Not confident'],
  quitImportance: ['Important'],
  quitWhen: ['This week'],
};

const mockPainUserData = {
  doctor: ['No'],
};

export const mockUserData = appVariant<UserData>({
  nerva: mockNervaUserData,
  meno: mockMenoUserData,
  quit: mockQuitUserData,
  pain: mockPainUserData,
});

type MockUserProperties = {
  user?: IncompleteUser | undefined;
  userData?: UserData | undefined;
};

/** Generate and sign-up a unique user. */
export async function createMockUser(
  from: MockUserProperties
): Promise<AssessmentUser | undefined> {
  const { user, userData } = from;
  // to allow us to identify users created by this createMockUser, we add 'dev-mock' as the 1st arg (default='dev'):
  // createMockUser is called by DemoScreens.tsx and SkipToPage.ts but
  // (unlike the in the integration tests) these users are not deleted after use in this code.
  const uniqueTestEmail = generateUniqueTestEmail('dev-mock');
  const { user: emailUser } = await Auth.signUpUser({
    email: user?.email ?? uniqueTestEmail,
    confirmEmail: user?.email ?? uniqueTestEmail,
    userData: userData ?? mockUserData,
    name: user?.name ?? mockName,
    lastName: user?.lastName ?? mockLastName,
    password: user?.tempPassword ?? mockPassword,
  });
  return emailUser as AssessmentUser;
}
